import { useEffect, useState } from 'react'
import { Loader2, X } from 'lucide-react'
import { usePendingMailSendStore, type PendingSendSnapshot } from '../../stores/pendingMailSendStore'

// Gmail-style "Message sent · Undo" snackbar.
// Lives at the bottom-left of the viewport (Gmail's exact position),
// fixed, non-blocking. The composer closes the moment the user clicks
// Send — this toast carries the 10s undo window so the user can keep
// reading mail / switch threads / close the page without being held
// hostage by a countdown in the compose pane.
//
// Visual design notes:
//   - Dark elevated card on light + dark themes (uses theme tokens)
//   - Slide-in from below; respects prefers-reduced-motion
//   - Subtle linear progress bar at the bottom edge ticks the
//     remaining window to 0 — gives the user a passive cue that the
//     undo opportunity is finite without shouting "10... 9... 8..."
//     numerically (Gmail's pattern)
//   - "Отменить" button uses text-primary so it reads as a clear
//     action affordance, not just label text
//   - × dismisses the toast AND commits the send immediately
//     (matches Gmail: closing the snackbar = "yes, send now")

interface Props {
  /**
   * Called when the user clicks Undo — receives the snapshot the
   * composer originally fired so the parent can reopen the composer
   * pre-loaded with the unsent content.
   */
  onUndo: (snap: PendingSendSnapshot) => void
}

export default function MailSentToast({ onUndo }: Props) {
  const pending = usePendingMailSendStore((s) => s.pending)
  const cancel = usePendingMailSendStore((s) => s.cancel)
  const fireNow = usePendingMailSendStore((s) => s.fireNow)
  // 4Hz tick to drive the progress bar's width smoothly. Cheap —
  // only renders this small component, not the dashboard.
  const [, force] = useState(0)
  useEffect(() => {
    if (!pending) return
    const id = setInterval(() => force((n) => n + 1), 100)
    return () => clearInterval(id)
  }, [pending])

  if (!pending) return null

  const remainingMs = Math.max(0, pending.scheduledAt - Date.now())
  const progressPct = Math.max(0, Math.min(100, (remainingMs / pending.delayMs) * 100))

  const handleUndo = () => {
    const snap = cancel()
    if (snap) onUndo(snap)
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 left-6 z-[110] pointer-events-none motion-safe:animate-in motion-safe:slide-in-from-bottom-2 motion-safe:fade-in motion-safe:duration-200"
    >
      <div className="pointer-events-auto relative overflow-hidden min-w-[320px] max-w-[420px] bg-card border border-border rounded-xl shadow-2xl flex items-center gap-3 pl-4 pr-2 py-3">
        <Loader2 size={16} className="text-primary animate-spin shrink-0" aria-hidden />
        <div className="flex-1 min-w-0 text-[13.5px] text-foreground font-medium truncate">
          Сообщение отправлено
        </div>
        <button
          type="button"
          onClick={handleUndo}
          className="px-3 py-1.5 text-[13px] font-semibold text-primary hover:bg-primary/10 rounded-md transition shrink-0"
        >
          Отменить
        </button>
        <button
          type="button"
          onClick={fireNow}
          className="w-7 h-7 rounded-md inline-flex items-center justify-center text-muted-foreground hover:bg-muted/60 transition shrink-0"
          aria-label="Отправить сейчас"
          title="Отправить сейчас"
        >
          <X size={14} />
        </button>
        {/* Bottom-edge linear countdown — purely passive cue */}
        <div className="absolute bottom-0 left-0 h-[2px] bg-primary/70 transition-[width] ease-linear" style={{ width: `${progressPct}%` }} />
      </div>
    </div>
  )
}
